/** @jsx jsx */

import { Fragment, ReactNode } from 'react';

import { Colors, Link, SkeletonText, Text } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import { getAppUrl } from 'app/developer-v3/utils';
import { useQueryIntegrationIssuesAll } from 'app/developer-v3/hooks/useQueryIssuesAll';
import { useAvroEvent, useRouter } from 'app/developer-v3/hooks';

const styleCardsList = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;
  gap: 20px;
  margin-bottom: 40px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1250px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`;

const styleCard = css`
  display: flex;
  flex-direction: column;
  border: 1px solid ${Colors.GrayWarm4};
  border-radius: 4px;
`;

const styleImage = css`
  padding: 20px 20px 0 20px;
`;

const styleCardBody = css`
  flex: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  border-top: 1px solid ${Colors.GrayWarm4};
`;

interface CardProps {
  imgSrc: string;
  title: string;
  description: ReactNode;
  ctaTitle: string;
  ctaUrl: string;
  interactionName: string;
  interactionGoal: string;
}

const Card = ({
  ctaTitle,
  ctaUrl,
  description,
  interactionGoal,
  interactionName,
  imgSrc,
  title,
}: CardProps) => {
  const { routeTo } = useRouter();
  const { emitPartnerUserInteractionEvent } = useAvroEvent();

  const handleClick = () => {
    emitPartnerUserInteractionEvent({
      eventAction: 'click',
      interactionGoal,
      interactionName,
    });

    routeTo(ctaUrl);
  };

  return (
    <div css={styleCard}>
      <img alt="" css={styleImage} height={160} src={imgSrc} width={320} />
      <div css={styleCardBody}>
        <div>
          <Text margin="0 0 10px" tag="h4" type="Body4">
            {title}
          </Text>
          <Text margin="0 0 20px" tag="div">
            {description}
          </Text>
        </div>
        <Link href={ctaUrl} onPress={handleClick} variant="primary">
          {ctaTitle}
        </Link>
      </div>
    </div>
  );
};

interface CardsChecklistProps {
  title?: string;
  order: 'normal' | 'reverse';
  appId: number;
}

export function CardsChecklist({ order, title, appId }: CardsChecklistProps) {
  const { data, isLoading } = useQueryIntegrationIssuesAll(
    {
      actions: [],
      integrationId: Number(appId),
      orderBy: 'priority',
      orderDirection: 'ASC',
    },
    {
      keepPreviousData: true,
      staleTime: 60000,
    }
  );
  const issues = data || [];
  const bugs = issues.filter(({ typeOfDisplay }) => typeOfDisplay === 'Bug');
  const featureRequests = issues.filter(
    ({ typeOfDisplay }) => typeOfDisplay === 'Feature Request'
  );
  const appUrl = getAppUrl(appId);

  const cards: CardProps[] = [
    {
      title: 'Create Zap Templates',
      description:
        'Make it easy for your users to start using your integration.',
      imgSrc: '/images/create-zap-templates.png',
      ctaTitle: 'Create Zap Templates',
      ctaUrl: `/zap-templates`,
      interactionName: 'create_zap_templates_button_clicked',
      interactionGoal: 'Navigate user to Zap Templates page',
    },
    {
      title: 'Grow with an Embed',
      description:
        'Grow your user base by providing automation solutions within your product.',
      imgSrc: '/images/embed-zapier.png',
      ctaTitle: 'Embed Zapier',
      ctaUrl: `${appUrl}/embed`,
      interactionName: 'embed_zapier_button_clicked',
      interactionGoal: 'Navigate user to Embed Zapier page',
    },
    {
      title: 'Monitor integration health',
      description: isLoading ? (
        <SkeletonText lines={2} textAlign="left" />
      ) : (
        'Track API requests, errors, and performance metrics with detailed event logs.'
      ),
      imgSrc: '/images/monitor-integration.png',
      ctaTitle: 'Monitor events',
      ctaUrl: `${appUrl}/monitoring`,
      interactionName: 'monitor_your_integration_button_clicked',
      interactionGoal: 'Navigate user to Monitoring page',
    },
    {
      title: 'Catch integration issues',
      description: isLoading ? (
        <SkeletonText lines={2} textAlign="left" />
      ) : (
        `You have ${bugs.length} bug(s) and ${featureRequests.length} feature request(s).`
      ),
      imgSrc: '/images/integration-issues.png',
      ctaTitle: 'View issues',
      ctaUrl: `${appUrl}/issues`,
      interactionName: 'manage_your_integration_button_clicked',
      interactionGoal: 'Navigate user to Issues page',
    },
  ];

  if (order === 'reverse') {
    cards.reverse();
  }

  return (
    <Fragment>
      {title && (
        <Text margin="10px 0 20px 0" tag="h3" type="ParagraphHeader3">
          {title}
        </Text>
      )}
      <div css={styleCardsList}>
        {cards.map((card, index) => (
          <Card key={index} {...card} />
        ))}
      </div>
    </Fragment>
  );
}
